import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bell, Check, X, AlertCircle, Info, CheckCircle, BellOff, Inbox, RefreshCw
} from 'react-feather';
import { useNotifications } from '../contexts/NotificationContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const NotificationsPage = () => {
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead,
    isConnected,
    refreshNotifications
  } = useNotifications();

  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter notifications based on active filter
  const filteredNotifications = notifications.filter(notification => {
    if (activeFilter === 'unread') return !notification.is_read;
    if (activeFilter === 'read') return notification.is_read;
    return true; // 'all' filter
  });

  // Load notifications on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await refreshNotifications();
      } catch (error) {
        console.error('Error loading notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [refreshNotifications]);

  const handleMarkAsRead = async (notificationId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;
    
    try {
      setIsMarkingAll(true);
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all as read:', error);
    } finally {
      setIsMarkingAll(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await refreshNotifications();
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'error':
        return <AlertCircle className="text-red-500 flex-shrink-0 mt-1" size={18} />;
      case 'success':
        return <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />;
      case 'warning':
        return <AlertCircle className="text-yellow-500 flex-shrink-0 mt-1" size={18} />;
      default:
        return <Info className="text-blue-500 flex-shrink-0 mt-1" size={18} />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
              <Bell className="w-8 h-8 text-blue-600 animate-pulse" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Chargement des notifications...</h2>
            <p className="text-gray-500">Veuillez patienter pendant que nous récupérons vos notifications.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Notifications
            </h2>
            <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
              <div className="mt-2 flex items-center text-sm text-gray-500">
                <Bell className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                {unreadCount > 0 ? (
                  <>
                    <span className="font-medium">{unreadCount}</span> notification{unreadCount > 1 ? 's' : ''} non lue{unreadCount > 1 ? 's' : ''}
                  </>
                ) : (
                  'Toutes les notifications sont lues'
                )}
              </div>
              <div className="mt-2 flex items-center text-sm text-gray-500">
                <div className={`w-2 h-2 rounded-full mr-1.5 ${isConnected ? 'bg-green-400' : 'bg-yellow-400'}`}></div>
                {isConnected ? 'Connecté en temps réel' : 'Hors ligne - Mise à jour en attente'}
              </div>
            </div>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3
          ">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <RefreshCw className={`-ml-1 mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0 || isMarkingAll}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                unreadCount > 0 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              {isMarkingAll ? 'Traitement...' : 'Tout marquer comme lu'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex space-x-4 border-b border-gray-200">
          <button
            onClick={() => setActiveFilter('all')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeFilter === 'all'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Toutes
          </button>
          <button
            onClick={() => setActiveFilter('unread')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeFilter === 'unread'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Non lues
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveFilter('read')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeFilter === 'read'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Lues
          </button>
        </div>

        {/* Notifications list */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Inbox className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune notification</h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeFilter === 'all'
                  ? 'Vous n\'avez pas encore de notifications.'
                  : activeFilter === 'unread'
                  ? 'Vous n\'avez pas de notifications non lues.'
                  : 'Vous n\'avez pas de notifications lues.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {filteredNotifications.map((notification) => (
                <li key={notification.id} className="hover:bg-gray-50">
                  <Link
                    to={notification.data?.url || '#'}
                    className="block hover:bg-gray-50"
                    onClick={(e) => {
                      if (!notification.is_read) {
                        handleMarkAsRead(notification.id, e);
                      }
                    }}
                  >
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {getIcon(notification.type || 'info')}
                          <p className={`ml-3 text-sm font-medium ${
                            !notification.is_read ? 'text-gray-900' : 'text-gray-500'
                          }`}>
                            {notification.title}
                          </p>
                        </div>
                        <div className="ml-2 flex-shrink-0 flex">
                          {!notification.is_read && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Nouveau
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                          <p className="flex items-center text-sm text-gray-500">
                            {notification.message}
                          </p>
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                          <time dateTime={notification.created_at}>
                            {format(new Date(notification.created_at), 'PPPp', { locale: fr })}
                          </time>
                        </div>
                      </div>
                      {notification.data && Object.keys(notification.data).length > 0 && (
                        <div className="mt-2 p-3 text-sm bg-gray-50 rounded-md border border-gray-100">
                          <pre className="whitespace-pre-wrap text-gray-600 text-xs">
                            {JSON.stringify(notification.data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
